import React, { useState, useEffect, useRef } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { TouchOptimizer, DeviceDetector } from '../utils/mobileOptimizer';

const Navigation = ({ mobileState }) => {
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const [activeSubmenu, setActiveSubmenu] = useState(null);
  const location = useLocation();
  const navRef = useRef();
  const menuToggleRef = useRef();

  // Debug: Log mobile state and check DOM
  useEffect(() => {
    // Check if button exists in DOM
    setTimeout(() => {
      const button = document.querySelector('.mobile-menu-toggle');
      if (button) {
        console.log('Mobile menu initialized successfully');
      }
    }, 100);
  }, [mobileState]);

  // Close mobile menu when route changes
  useEffect(() => {
    setIsMobileMenuOpen(false);
    setActiveSubmenu(null);
    document.body.classList.remove('mobile-menu-open');
  }, [location]);

  // Apply touch optimizations to navigation elements
  useEffect(() => {
    if (mobileState?.device.isTouchDevice) {
      const navLinks = navRef.current?.querySelectorAll('a');
      const menuToggle = menuToggleRef.current;
      
      const cleanupFunctions = [];
      
      // Optimize menu toggle button
      if (menuToggle) {
        TouchOptimizer.optimizeElement(menuToggle);
        const cleanup = TouchOptimizer.addTouchFeedback(menuToggle, {
          activeClass: 'touch-active',
          duration: 200
        });
        if (cleanup) cleanupFunctions.push(cleanup);
      }
      
      // Optimize navigation links
      navLinks?.forEach(link => {
        TouchOptimizer.optimizeElement(link);
        const cleanup = TouchOptimizer.addTouchFeedback(link, {
          activeClass: 'nav-link-active',
          duration: 150
        });
        if (cleanup) cleanupFunctions.push(cleanup);
      });
      
      return () => {
        cleanupFunctions.forEach(cleanup => cleanup());
      };
    }
  }, [mobileState]);

  // Enhanced mobile menu toggle with touch feedback
  const toggleMobileMenu = () => {
    const newState = !isMobileMenuOpen;
    setIsMobileMenuOpen(newState);
    setActiveSubmenu(null); // Close any open submenus
    
    if (newState) {
      document.body.classList.add('mobile-menu-open');
      // Prevent background scrolling on mobile
      if (mobileState?.device.isMobile) {
        document.body.style.overflow = 'hidden';
        document.body.style.position = 'fixed';
        document.body.style.width = '100%';
      }
    } else {
      document.body.classList.remove('mobile-menu-open');
      // Restore scrolling
      document.body.style.overflow = '';
      document.body.style.position = '';
      document.body.style.width = '';
    }
  };

  // Handle submenu toggle
  const toggleSubmenu = (menuKey, event) => {
    event.preventDefault();
    event.stopPropagation();
    setActiveSubmenu(activeSubmenu === menuKey ? null : menuKey);
  };

  // Enhanced click outside handling with touch support
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (isMobileMenuOpen && event.target && typeof event.target.closest === 'function' && !event.target.closest('.navbar')) {
        setIsMobileMenuOpen(false);
        setActiveSubmenu(null);
        document.body.classList.remove('mobile-menu-open');
        // Restore scrolling
        document.body.style.overflow = '';
        document.body.style.position = '';
        document.body.style.width = '';
      }
    };

    const handleTouchOutside = (event) => {
      if (isMobileMenuOpen && event.target && typeof event.target.closest === 'function' && !event.target.closest('.navbar')) {
        event.preventDefault();
        handleClickOutside(event);
      }
    };

    document.addEventListener('click', handleClickOutside);
    if (mobileState?.device.isTouchDevice) {
      document.addEventListener('touchstart', handleTouchOutside, { passive: false });
    }
    
    return () => {
      document.removeEventListener('click', handleClickOutside);
      document.removeEventListener('touchstart', handleTouchOutside);
    };
  }, [isMobileMenuOpen, mobileState]);

  // Clean up body styles on unmount
  useEffect(() => {
    return () => {
      document.body.classList.remove('mobile-menu-open');
      document.body.style.overflow = '';
      document.body.style.position = '';
      document.body.style.width = '';
    };
  }, []);

  const isActive = (path) => {
    return location.pathname === path ? 'active' : '';
  };

  // Menu structure with submenus
  //
  // Website to search for Awesome FOnts
  // https://fontawesome.com/search?ic=free-collection
  //
  const menuItems = [
    {
      key: 'home',
      path: '/',
      icon: 'fas fa-home',
      text: 'Home'
    },
    {
      key: 'Rrossetti',
      text: 'Rossetti',
      icon: 'fas fa-users',
      hasSubmenu: true,
      submenu: [
        {
          path: '/calendar',
          icon: 'fas fa-calendar-alt',
          text: 'Calendar'
        },
        {
          path: '/family-stats',
          icon: 'fas fa-chart-bar',
          text: 'Family Stats'
        },
        {
          path: '/family-tree',
          icon: '/images/rossetti.png',
          text: 'Family Tree',
          isImage: true
        },
        {
          path: '/rossetti',
          icon: '/images/rossetti-coat-of-arms.jpg',
          text: 'Heritage',
          isImage: true
        }
      ]
    },
    {
      key: 'events',
      text: 'Events',
      icon: 'fas fa-calendar-check',
      hasSubmenu: true,
      submenu: [
        {
          path: '/april-12-2025',
          icon: 'fas fa-calendar-day',
          text: 'April 12, 2025'
        },
        {
          path: '/thanksgiving',
          icon: 'fas fa-tv',
          text: 'Thanksgiving'
        },
        {
          path: '/weddings',
          icon: 'fas fa-heart',
          text: 'Weddings'
        }
      ]
    },
    {
      key: 'food',
      text: 'Food & Drink',
      icon: 'fas fa-utensils',
      hasSubmenu: true,
      submenu: [
        {
          path: '/pane',
          icon: '/images/pane.png',
          text: 'Pane',
          isImage: true
        },
        {
          path: '/vino',
          icon: '/images/wine.png',
          text: 'Vino',
          isImage: true
        },
        {
          path: '/tomatojars',
          icon: 'fas fa-jar',
          text: 'Tomato Jars'
        },
        {
          path: '/genes',
          icon: 'fas fa-utensils',
          text: 'Gene\'s Restaurant'
        }
      ]
    },
    {
      key: 'places',
      text: 'Places',
      icon: 'fas fa-map-marker-alt',
      hasSubmenu: true,
      submenu: [
        {
          path: '/southington',
          icon: '/images/apple.png',
          text: 'Southington',
          isImage: true
        },
        {
          path: '/plymouth',
          icon: 'fas fa-mountain',
          text: 'Plymouth'
        },
        {
          path: '/rawrock',
          icon: 'fas fa-person-hiking',
          text: 'Raw Rock'
        }
      ]
    },
    {
      key: 'activities',
      text: 'Activities',
      icon: 'fas fa-gamepad',
      hasSubmenu: true,
      submenu: [
        {
          path: '/futbol',
          icon: '/images/futbol.png',
          text: 'Futbol',
          isImage: true
        },
        {
          path: '/jeep',
          icon: '/images/jeep.png',
          text: 'Jeep',
          isImage: true
        },
        {
          path: '/travel',
          icon: 'fas fa-plane',
          text: 'Travel'
        }
      ]
    }
  ];

  // Render menu item (with or without submenu)
  const renderMenuItem = (item) => {
    if (item.hasSubmenu) {
      return (
        <li key={item.key} className="nav-item-with-submenu">
          <button
            className={`nav-link submenu-toggle ${activeSubmenu === item.key ? 'submenu-open' : ''} ${mobileState?.device.isTouchDevice ? 'touch-target' : ''}`}
            onClick={(e) => toggleSubmenu(item.key, e)}
            aria-expanded={activeSubmenu === item.key}
            aria-haspopup="true"
          >
            {item.isImage ? (
              <img src={item.icon} alt={item.text} className="nav-icon" />
            ) : (
              <i className={`${item.icon} nav-icon`}></i>
            )}
            <span className="nav-text">{item.text}</span>
            <i className={`fas fa-chevron-down submenu-arrow ${activeSubmenu === item.key ? 'rotated' : ''}`}></i>
          </button>
          <ul className={`submenu ${activeSubmenu === item.key ? 'submenu-open' : ''}`}>
            {item.submenu.map((subItem, index) => (
              <li key={index}>
                <Link
                  to={subItem.path}
                  className={`nav-link submenu-link ${isActive(subItem.path)} ${mobileState?.device.isTouchDevice ? 'touch-target' : ''}`}
                >
                  {subItem.isImage ? (
                    <img src={subItem.icon} alt={subItem.text} className="nav-icon" />
                  ) : (
                    <i className={`${subItem.icon} nav-icon`}></i>
                  )}
                  <span className="nav-text">{subItem.text}</span>
                </Link>
              </li>
            ))}
          </ul>
        </li>
      );
    } else {
      return (
        <li key={item.key}>
          <Link
            to={item.path}
            className={`nav-link ${isActive(item.path)} ${mobileState?.device.isTouchDevice ? 'touch-target' : ''}`}
          >
            {item.isImage ? (
              <img src={item.icon} alt={item.text} className="nav-icon" />
            ) : (
              <i className={`${item.icon} nav-icon`}></i>
            )}
            <span className="nav-text">{item.text}</span>
          </Link>
        </li>
      );
    }
  };

  return (
    <nav 
      className={`navbar ${mobileState?.device.type || ''} ${mobileState?.device.isTouchDevice ? 'touch-enabled' : ''}`}
      ref={navRef}
    >
      <div className="navbar-container">
        <div className="navbar-brand">
          {/* Brand removed to avoid menu interference */}
        </div>
        
        <button 
          ref={menuToggleRef}
          className={`mobile-menu-toggle ${isMobileMenuOpen ? 'active' : ''} ${mobileState?.device.isTouchDevice ? 'touch-optimized' : ''}`}
          onClick={toggleMobileMenu}
          aria-label="Toggle mobile menu"
          aria-expanded={isMobileMenuOpen}
        >
          <span></span>
          <span></span>
          <span></span>
        </button>
        
        <ul className={`navbar-menu ${isMobileMenuOpen ? 'mobile-open' : ''} ${mobileState?.device.type || ''}`}>
          {menuItems.map(renderMenuItem)}
        </ul>
      </div>
    </nav>
  );
};

export default Navigation;
